import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth, hasProfessionalAccess } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Home from './pages/Home';
import SearchResults from './pages/SearchResults';
import PropertyDetail from './pages/PropertyDetail';
import PublishProperty from './pages/PublishProperty';
import NewsPage from './pages/NewsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import MortgageCalculatorPage from './pages/MortgageCalculatorPage';
import ValoraGratis from './pages/ValoraGratis';
import Pricing from './pages/Pricing';
import Checkout from './pages/Checkout';
import MyAccount from './pages/MyAccount';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import AdminDashboard from './pages/AdminDashboard';
import AdminExecutiveDashboard from './pages/AdminExecutiveDashboard';
import AdminGrowthV2 from './pages/AdminGrowthV2';
import AdminAdvisorInspector from './pages/AdminAdvisorInspector';
import AdvisorProductDashboard from './pages/AdvisorProductDashboard';
import AdvisorGuidedStart from './pages/AdvisorGuidedStart';
import AdvisorMetricsPage from './pages/AdvisorMetricsPage';
import ComparePage from './pages/ComparePage';
import DecisionWorkspacePage from './pages/DecisionWorkspacePage';
import CRMInbox from './pages/CRMInbox';
import CRMLeadDetail from './pages/CRMLeadDetail';
import IntegrationsCenter from './pages/IntegrationsCenter';
import ImportWizard from './pages/ImportWizard';
import { DataPolicy, Privacy, Terms, CookiePolicy } from './pages/legal/LegalPages';
import PrivacyCenter from './pages/PrivacyCenter';
import HabeasData from './pages/HabeasData';
import CookieBanner from './components/CookieBanner';
import AgencyProfile from './pages/AgencyProfile';
import AgencyDashboard from './pages/AgencyDashboard';
import AgentProfile from './pages/AgentProfile';
import AgentDashboard from './pages/AgentDashboard';
import DeveloperDashboard from './pages/DeveloperDashboard';
import DeveloperProjectForm from './pages/DeveloperProjectForm';
import DeveloperProjectDetail from './pages/DeveloperProjectDetail';
import AgentDirectory from './pages/AgentDirectory';
import AgencyDirectory from './pages/AgencyDirectory';
import DeveloperProfile from './pages/DeveloperProfile';
import ConstructorPanel from './pages/ConstructorPanel';
import ProjectProfile from './pages/ProjectProfile';
import ProjectsDirectory from './pages/ProjectsDirectory';
import ForAgenciesPage from './pages/ForAgenciesPage';
import ShortLink from './pages/ShortLink';
import { Toaster } from './components/ui/toaster';
import { initAnalytics, trackPage } from './lib/analytics';
import BetaBanner from './components/BetaBanner';

function Protected({ children, role, professional }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  // `role` accepts a string or an array of allowed roles. When `professional`
  // is set, a role='user' with an active pro plan is also allowed (capability
  // gating decoupled from the DB role).
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    const ok = allowed.includes(user.role) || (professional && hasProfessionalAccess(user));
    if (!ok) return <Navigate to="/" replace />;
  }
  return children;
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => { trackPage(location.pathname); }, [location.pathname]);
  return null;
}

// Ocultamos overlays de chrome (BetaBanner + CookieBanner) en superficies
// inmersivas del Advisor donde compiten con la conversación. Se mantienen en
// el resto de la app para reforzar propuesta comercial y cumplir cookie law.
const IMMERSIVE_PATHS = ['/advisor/inicio'];

function ImmersiveGate({ children }) {
  const location = useLocation();
  const immersive = IMMERSIVE_PATHS.some((p) => location.pathname.startsWith(p));
  if (immersive) return null;
  return children;
}

function App() {
  useEffect(() => { initAnalytics(); }, []);

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <div className="App">
          <BrowserRouter>
            <AuthProvider>
              <PageTracker />
              <ImmersiveGate><BetaBanner /></ImmersiveGate>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/buscar" element={<SearchResults />} />
                <Route path="/propiedad/:id" element={<PropertyDetail />} />
                <Route path="/p/:id" element={<ShortLink />} />
                <Route path="/publicar" element={<ErrorBoundary><Protected><PublishProperty /></Protected></ErrorBoundary>} />
                <Route path="/panel-constructoras" element={<ConstructorPanel />} />
                <Route path="/editar/:id" element={<ErrorBoundary><Protected><PublishProperty /></Protected></ErrorBoundary>} />
                <Route path="/noticias" element={<NewsPage />} />
                <Route path="/prensa" element={<NewsPage />} />
                <Route path="/sobre-nosotros" element={<AboutPage />} />
                <Route path="/contacto" element={<ContactPage />} />
                <Route path="/calculadora" element={<MortgageCalculatorPage />} />
                <Route path="/valorar-gratis" element={<ErrorBoundary><ValoraGratis /></ErrorBoundary>} />
                <Route path="/precios" element={<Pricing />} />
                <Route path="/checkout/:planId" element={<Protected><Checkout /></Protected>} />
                <Route path="/mi-cuenta" element={<Protected><MyAccount /></Protected>} />
                <Route path="/mi-cuenta/inmobiliaria" element={<Protected><AgencyDashboard /></Protected>} />
                <Route path="/mi-cuenta/perfil-profesional" element={<Protected><AgentDashboard /></Protected>} />
                <Route path="/mi-constructora" element={<Protected role="developer"><DeveloperDashboard /></Protected>} />
                <Route path="/mi-constructora/proyecto/nuevo" element={<Protected role="developer"><DeveloperProjectForm /></Protected>} />
                <Route path="/mi-constructora/proyecto/:id/editar" element={<Protected role="developer"><DeveloperProjectForm /></Protected>} />
                <Route path="/mi-constructora/proyecto/:id" element={<Protected role="developer"><DeveloperProjectDetail /></Protected>} />
                <Route path="/inmobiliaria/:slug" element={<AgencyProfile />} />
                <Route path="/agente/:slug" element={<AgentProfile />} />
                <Route path="/agentes" element={<AgentDirectory />} />
                <Route path="/inmobiliarias" element={<AgencyDirectory />} />
                <Route path="/proyectos" element={<ProjectsDirectory />} />
                <Route path="/proyecto/:slug" element={<ProjectProfile />} />
                <Route path="/constructora/:slug" element={<DeveloperProfile />} />
                <Route path="/para-inmobiliarias" element={<ForAgenciesPage />} />
                <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
                <Route path="/admin/dashboard" element={<Protected role="admin"><AdminExecutiveDashboard /></Protected>} />
                <Route path="/admin/growth-v2" element={<Protected role="admin"><AdminGrowthV2 /></Protected>} />
                <Route path="/admin/advisor" element={<Protected role="admin"><AdminAdvisorInspector /></Protected>} />
                <Route path="/admin/advisor/dashboard" element={<Protected role="admin"><AdvisorProductDashboard /></Protected>} />
                <Route path="/admin/advisor/metrics" element={<Protected role="admin"><AdvisorMetricsPage /></Protected>} />
                <Route path="/comparar" element={<ComparePage />} />
                <Route path="/mi-decision" element={<DecisionWorkspacePage />} />
                <Route path="/advisor/inicio" element={<AdvisorGuidedStart />} />
                <Route path="/crm" element={<Protected role={['admin', 'agency', 'agent']} professional><CRMInbox /></Protected>} />
                <Route path="/crm/lead/:id" element={<Protected role={['admin', 'agency', 'agent']} professional><CRMLeadDetail /></Protected>} />
                <Route path="/crm/integraciones" element={<Protected role={['admin', 'agency']}><IntegrationsCenter /></Protected>} />
                <Route path="/crm/integraciones/importar" element={<Protected role={['admin', 'agency', 'agent']} professional><ImportWizard /></Protected>} />
                {/* Iter 44 · Legal + Privacy */}
                <Route path="/politica-datos" element={<DataPolicy />} />
                <Route path="/privacidad" element={<Privacy />} />
                <Route path="/terminos" element={<Terms />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/habeas-data" element={<HabeasData />} />
                <Route path="/mi-cuenta/privacidad" element={<Protected><PrivacyCenter /></Protected>} />
                <Route path="/login" element={<Login />} />
                <Route path="/registro" element={<Register />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/recuperar" element={<ForgotPassword />} />
                <Route path="/restablecer" element={<ResetPassword />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <ImmersiveGate><CookieBanner /></ImmersiveGate>
            </AuthProvider>
          </BrowserRouter>
          <Toaster />
        </div>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
